import { useState, type FormEvent } from 'react';
import { ApiError, post } from '../api.js';
import { PageTitle } from '../frame.js';
import { t } from '../i18n.js';
import { useSession } from '../session.js';

/** The same rule as `POST /api/auth/password`: ten characters or more. */
export const MIN_PASSWORD_LENGTH = 10;

/** The screen behind the footer's "Change Password": the signed-in user changes their own password. */
export function PasswordPage() {
  const { session } = useSession();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [repeat, setRepeat] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const tooShort = next.length > 0 && next.length < MIN_PASSWORD_LENGTH;
  const mismatch = repeat.length > 0 && repeat !== next;
  const ready = current.length > 0 && next.length >= MIN_PASSWORD_LENGTH && repeat === next;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!ready) return;
    setBusy(true);
    setError(null);
    setDone(false);
    try {
      await post('/api/auth/password', { current, next });
      setDone(true);
      setCurrent('');
      setNext('');
      setRepeat('');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <PageTitle title={t('changePassword')} hint={`${t('user')}: ${session?.user.username ?? ''}`} />
      <form onSubmit={submit} className="card" style={{ maxWidth: 520 }}>
        <div className="form-grid" style={{ gridTemplateColumns: 'max-content 1fr' }}>
          <label htmlFor="pw-current">{t('currentPassword')} :</label>
          <input id="pw-current" type="password" autoFocus maxLength={200} autoComplete="current-password" value={current} onChange={(e) => setCurrent(e.target.value)} />
          <label htmlFor="pw-next">{t('newPassword')} :</label>
          <input id="pw-next" type="password" maxLength={200} autoComplete="new-password" aria-invalid={tooShort || undefined} value={next} onChange={(e) => setNext(e.target.value)} />
          <label htmlFor="pw-repeat">{t('repeatPassword')} :</label>
          <input id="pw-repeat" type="password" maxLength={200} autoComplete="new-password" aria-invalid={mismatch || undefined} value={repeat} onChange={(e) => setRepeat(e.target.value)} />
          <span />
          <div>
            <button className="btn primary" disabled={busy || !ready}>
              {t('changePassword')}
            </button>
            {tooShort && (
              <span className="status warn" style={{ marginLeft: 10 }}>
                {t('passwordRule')}
              </span>
            )}
            {!tooShort && mismatch && (
              <span className="status warn" style={{ marginLeft: 10 }}>
                {t('passwordMismatch')}
              </span>
            )}
            {done && (
              <span className="status ok" style={{ marginLeft: 10 }}>
                {t('passwordChanged')}
              </span>
            )}
            {error && (
              <span className="status err" style={{ marginLeft: 10 }}>
                {error}
              </span>
            )}
          </div>
        </div>
      </form>
      <p className="tip">{t('passwordRule')}</p>
    </>
  );
}
